import { useState } from "react";
import { api, formatDuration, type MediaRow } from "../api.ts";
import { MediaPlayerModal } from "./MediaPlayerModal.tsx";

export function MediaTable({
  project,
  media,
  onRemoved,
}: {
  project: string;
  media: MediaRow[];
  onRemoved: () => void;
}) {
  const [removingId, setRemovingId] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [playing, setPlaying] = useState<MediaRow | null>(null);

  const q = query.trim().toLowerCase();
  const filtered =
    q === ""
      ? media
      : media.filter(
          (m) =>
            m.filename.toLowerCase().includes(q) ||
            m.tags.some(
              (t) => t.key.toLowerCase().includes(q) || t.value.toLowerCase().includes(q),
            ),
        );

  async function onRemove(row: MediaRow) {
    if (!confirm(`Remove "${row.filename}" and its fingerprints?`)) return;
    setRemovingId(row.id);
    setError(null);
    try {
      await api.removeMedia(project, row.id);
      onRemoved();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setRemovingId(null);
    }
  }

  if (media.length === 0) {
    return <p className="text-sm text-slate-500">No media yet. Use “Add Media” to upload a file.</p>;
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search by filename or tag…"
          className="w-full max-w-sm rounded-lg border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100 outline-none focus:border-indigo-500"
        />
        <span className="shrink-0 text-xs text-slate-500">
          {q === "" ? `${media.length} files` : `${filtered.length} of ${media.length}`}
        </span>
      </div>

      <div className="overflow-hidden rounded-xl border border-slate-800">
        {error && <p className="bg-rose-950/40 px-4 py-2 text-sm text-rose-400">{error}</p>}
        <table className="w-full text-left text-sm">
          <thead className="bg-slate-900/80 text-xs uppercase tracking-wide text-slate-500">
            <tr>
              <th className="w-12 px-4 py-3 font-medium">#</th>
              <th className="px-4 py-3 font-medium">File</th>
              <th className="px-4 py-3 font-medium">Tags</th>
              <th className="px-4 py-3 font-medium">Duration</th>
              <th className="px-4 py-3 font-medium">Hashes</th>
              <th className="px-4 py-3" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-800">
            {filtered.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-6 text-center text-slate-500">
                  No files match “{query.trim()}”.
                </td>
              </tr>
            ) : (
              filtered.map((row, idx) => (
                <tr key={row.id} className="hover:bg-slate-900/40">
                  <td className="px-4 py-3 tabular-nums text-slate-500">{idx + 1}</td>
              <td className="px-4 py-3">
                <button
                  onClick={() => setPlaying(row)}
                  title="Play"
                  className="text-left text-indigo-400 hover:underline"
                >
                  {row.filename}
                </button>
              </td>
              <td className="px-4 py-3">
                <div className="flex flex-wrap gap-1">
                  {row.tags.length === 0 ? (
                    <span className="text-slate-600">—</span>
                  ) : (
                    row.tags.map((t, i) => (
                      <span
                        key={i}
                        className="rounded-md bg-slate-800 px-2 py-0.5 text-xs text-slate-300"
                      >
                        <span className="text-slate-500">{t.key}:</span> {t.value}
                      </span>
                    ))
                  )}
                </div>
              </td>
              <td className="px-4 py-3 tabular-nums text-slate-300">
                {formatDuration(row.durationSeconds)}
              </td>
              <td className="px-4 py-3 tabular-nums text-slate-400">
                {row.hashCount.toLocaleString()}
              </td>
              <td className="px-4 py-3 text-right">
                <button
                  onClick={() => onRemove(row)}
                  disabled={removingId === row.id}
                  className="rounded-md border border-slate-700 px-2 py-1 text-xs text-rose-400 hover:border-rose-500/60 hover:bg-rose-950/30 disabled:opacity-40"
                >
                  {removingId === row.id ? "Removing…" : "Remove"}
                </button>
              </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {playing && (
        <MediaPlayerModal
          project={project}
          media={playing}
          onClose={() => setPlaying(null)}
        />
      )}
    </div>
  );
}
